import { ReactNode } from 'react'
import { RECIPE_FORM_DATA } from 'src/interfaces/Template.interface'
import { PriceRuleFormValue } from '../hooks/usePriceModifierFields'
import { isValidNumericString } from '../../utils/priceModifierValidation'
import { PLATFORM_FIELD } from 'src/interfaces/Platform.interface'

const truncate = (value: string, max = 48) =>
  value.length > max ? `${value.slice(0, max)}…` : value

const fieldLabel = (fieldId: string, nativeFields: PLATFORM_FIELD[]) => {
  const match = nativeFields.find((f) => String(f.id) === String(fieldId))
  return (match?.display_name ?? match?.api_name ?? fieldId) || 'Field'
}

export const buildProfileIdentitySummary = (
  ruleName: string,
  channelName: string | undefined,
  status: 'ACTIVE' | 'DRAFT'
): ReactNode => (
  <>
    Name: <span className="product-listing__summary-accent">{ruleName.trim() || 'Auto-generated'}</span>
    {' • '}
    Channel:{' '}
    <span className="product-listing__summary-success">{channelName ?? 'Channel'}</span>
    {' • '}
    Status:{' '}
    <span className={status === 'ACTIVE' ? 'product-listing__summary-success' : 'product-listing__summary-warn'}>
      {status === 'ACTIVE' ? 'Active' : 'Draft'}
    </span>
  </>
)

export const buildProductCriteriaSummary = (
  values: RECIPE_FORM_DATA | undefined,
  nativeFields: PLATFORM_FIELD[]
): ReactNode => {
  const recipes = values?.productRecipes ?? []
  const first = recipes[0]
  if (!first?.checkPlatformField && !first?.checkValue) {
    return (
      <>
        Add Filters to choose which Products will be created/updated to selected Channel
      </>
    )
  }
  const field = fieldLabel(first.checkPlatformField, nativeFields)
  const op = first.checkOperator?.replace(/_/g, ' ') ?? 'contains'
  const val = first.checkValue || '…'
  const extra = recipes.length > 1 ? ` (+${recipes.length - 1} more)` : ''
  return (
    <>
      Rule:{' '}
      <span className="product-listing__summary-accent">
        {field} {op.toLowerCase()} {val}
        {extra}
      </span>
    </>
  )
}

export const buildTitleDescriptionSummary = (titleInput: string): ReactNode => (
  <>
    Title pattern:{' '}
    <span className="product-listing__summary-accent">
      {titleInput.trim() ? truncate(titleInput.trim()) : 'Not configured'}
    </span>
  </>
)

export const buildPriceModifierSummary = (
  priceRules: PriceRuleFormValue[],
  repricing: { type: string; value: number | null },
  priceAdjustmentType: string
): ReactNode => {
  const activeRule = priceRules.find(
    (r) =>
      (r.adjustmentType &&
        r.adjustmentType !== 'NO_CHANGE' &&
        isValidNumericString(String(r.adjustmentValue ?? ''))) ||
      isValidNumericString(String(r.defaultValue ?? ''))
  )
  if (activeRule) {
    if (
      activeRule.adjustmentType !== 'NO_CHANGE' &&
      isValidNumericString(String(activeRule.adjustmentValue ?? ''))
    ) {
      const sign = activeRule.adjustmentType === 'DECREASE' ? '-' : '+'
      const unit = activeRule.adjustmentMode === 'PERCENTAGE' ? '%' : ''
      return (
        <>
          Modifier:{' '}
          <span className="product-listing__summary-success">
            {sign}
            {activeRule.adjustmentValue}
            {unit} on {activeRule.priceType || 'price'}
          </span>
        </>
      )
    }
    if (isValidNumericString(String(activeRule.defaultValue ?? ''))) {
      return (
        <>
          Default value:{' '}
          <span className="product-listing__summary-success">
            {activeRule.defaultValue} on {activeRule.priceType || 'price'}
          </span>
        </>
      )
    }
  }
  if (repricing.value == null || !Number.isFinite(repricing.value)) {
    return <>No price adjustment applied.</>
  }
  const val = Math.abs(repricing.value)
  if (val === 0) {
    const sign = priceAdjustmentType === 'decrease' ? '-' : '+'
    const unit = repricing.type === 'PERCENTAGE' ? '%' : ' flat'
    return (
      <>
        Modifier:{' '}
        <span className="product-listing__summary-success">
          {sign}0{unit}
        </span>
      </>
    )
  }
  const sign = priceAdjustmentType === 'decrease' ? '-' : '+'
  const unit = repricing.type === 'PERCENTAGE' ? '%' : ' flat'
  return (
    <>
      Modifier:{' '}
      <span className="product-listing__summary-success">
        {sign}
        {val}
        {unit}
      </span>{' '}
      applied to catalog price
    </>
  )
}

export const buildCategorySummary = (categoryPath: string): ReactNode =>
  categoryPath ? (
    <>
      Selected chain: <span className="product-listing__summary-accent">{categoryPath}</span>
    </>
  ) : (
    <>Select a marketplace category chain.</>
  )

export const buildMoreMappingSummary = (displayName: string): ReactNode => (
  <>
    Field: <span className="product-listing__summary-accent">{displayName}</span>
  </>
)

export const buildMoreMappingsSectionSummary = (fieldCount: number): ReactNode => {
  if (fieldCount <= 0) {
    return <>Configure remaining marketplace field mappings.</>
  }
  return (
    <>
      <span className="product-listing__summary-accent">{fieldCount}</span>
      {fieldCount === 1 ? ' field' : ' fields'} to map
    </>
  )
}

export const indexToSectionLetter = (index: number) => String.fromCharCode(65 + index)
